import { useState } from "react";
import { Link } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { UUID_RE, type MessagePoll, type MessagePollVoters } from "@atrium/shared";
import { api, errorMessage, qs } from "../../lib/api";
import { cn } from "../../lib/cn";
import { fullDate } from "../../lib/format";
import { useNow } from "../../lib/hooks";
import { Avatar } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Spinner } from "../../components/ui/Spinner";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconCheck, IconPoll, IconUsers } from "../../components/icons";

function timeLeft(endsAt: string): string {
  const ms = new Date(endsAt).getTime() - Date.now();
  const m = Math.max(1, Math.round(ms / 60_000));
  if (m < 60) return `${m}m left`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h left`;
  return `${Math.round(h / 24)}d left`;
}

/** Everyone who picked one answer, 50 at a time. */
function VoterList({ messageId, optionId }: { messageId: string; optionId: string }) {
  const q = useInfiniteQuery({
    queryKey: ["poll-voters", messageId, optionId],
    queryFn: ({ pageParam }) => api.get<MessagePollVoters>(`/api/messages/${messageId}/poll/voters${qs({ optionId, after: pageParam as string | undefined })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (l) => l.nextCursor ?? undefined,
    staleTime: 15_000,
  });
  const users = q.data?.pages.flatMap((p) => p.items) ?? [];
  if (q.isPending)
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  if (q.isError) return <p className="px-2 py-6 text-center text-sm text-fg-3">{errorMessage(q.error, "Couldn't load the votes")}</p>;
  if (!users.length) return <p className="px-2 py-6 text-center text-sm text-fg-3">No one picked this answer yet.</p>;
  return (
    <ul className="space-y-px" aria-label="Voters">
      {users.map((u) => (
        <li key={u.id}>
          <Link to={`/u/${u.username}`} className="flex h-11 items-center gap-2.5 rounded-md px-2 hover:bg-surface-2">
            <Avatar src={u.avatarUrl} name={u.displayName} size="sm" />
            <span className="min-w-0 truncate text-sm font-medium text-fg">{u.displayName}</span>
            <span className="min-w-0 truncate text-xs text-fg-3">@{u.username}</span>
          </Link>
        </li>
      ))}
      {q.hasNextPage && (
        <li className="pt-1">
          <Button size="sm" variant="ghost" className="w-full" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Show more
          </Button>
        </li>
      )}
    </ul>
  );
}

/** Who voted for what: answers on one side (with counts), that answer's voters on the other. */
function PollVotesDialog({ messageId, poll, optionId, onOptionChange, onClose }: { messageId: string; poll: MessagePoll; optionId: string | null; onOptionChange: (id: string) => void; onClose: () => void }) {
  const current = poll.options.find((o) => o.id === optionId) ?? poll.options[0];
  return (
    <Dialog open={optionId !== null} onOpenChange={(o) => !o && onClose()} title="Poll votes" description={poll.question} size="lg">
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="no-scrollbar flex shrink-0 gap-1 overflow-x-auto sm:w-48 sm:flex-col sm:overflow-visible" role="tablist" aria-label="Answers">
          {poll.options.map((o) => {
            const active = o.id === current.id;
            return (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onOptionChange(o.id)}
                className={cn("flex h-9 shrink-0 items-center gap-2 rounded-md px-2.5 text-left text-sm", active ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface")}
              >
                {o.emoji && <span className="text-base leading-none">{o.emoji}</span>}
                <span className="min-w-0 flex-1 truncate">{o.text}</span>
                <span className="text-xs text-fg-3 tabular">{o.votes}</span>
              </button>
            );
          })}
        </div>
        <div className="max-h-[50dvh] min-h-40 min-w-0 flex-1 overflow-y-auto" role="tabpanel" aria-label={current.text}>
          <VoterList messageId={messageId} optionId={current.id} />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A poll on a chat message. Before voting, answers are selectable rows
 * (single choice votes on click; several-answer polls collect picks and
 * submit with Vote). Results show once you've voted or the poll has ended;
 * from then on each answer (and "View votes") opens the list of who picked it.
 */
export function PollCard({
  messageId,
  poll,
  canVote,
  canEnd,
  onVote,
  onEnd,
}: {
  /** The message the poll is on; voters can't be listed while it's still sending. */
  messageId?: string;
  poll: MessagePoll;
  canVote: boolean;
  canEnd: boolean;
  onVote: (optionIds: string[]) => Promise<void>;
  onEnd: () => Promise<void>;
}) {
  useNow(30_000);
  const [picks, setPicks] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const closed = poll.closed || new Date(poll.endsAt) <= new Date();
  const voted = poll.myVotes.length > 0;
  const showResults = closed || voted || !canVote;
  const totalVotes = poll.options.reduce((n, o) => n + o.votes, 0);
  const leader = Math.max(0, ...poll.options.map((o) => o.votes));
  const [votesOf, setVotesOf] = useState<string | null>(null);
  const canListVoters = showResults && !!messageId && UUID_RE.test(messageId) && poll.totalVoters > 0;

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch {
      /* the action reports its own error */
    } finally {
      setBusy(false);
    }
  };
  const vote = (ids: string[]) =>
    run(async () => {
      await onVote(ids);
      setPicks([]);
    });

  return (
    <div className="mt-1.5 w-full max-w-[460px] rounded-lg border border-line bg-surface p-3" role="group" aria-label={`Poll: ${poll.question}`}>
      <div className="mb-0.5 text-base font-semibold text-fg">{poll.question}</div>
      <div className="mb-2.5 flex items-center gap-1.5 text-xs text-fg-3">
        <IconPoll className="icon size-3.5" aria-hidden />
        {closed ? "Poll ended" : poll.multiple ? "Choose one or more" : "Choose one"}
      </div>
      <div className="space-y-1.5" role={showResults ? "list" : poll.multiple ? "group" : "radiogroup"} aria-label="Answers">
        {poll.options.map((o) => {
          const pct = totalVotes ? Math.round((o.votes / totalVotes) * 100) : 0;
          const mine = poll.myVotes.includes(o.id);
          const top = o.votes === leader && o.votes > 0;
          if (showResults)
            return (
              <div
                key={o.id}
                role="listitem"
                onClick={canListVoters && o.votes > 0 ? () => setVotesOf(o.id) : undefined}
                title={canListVoters && o.votes > 0 ? "See who voted for this" : undefined}
                className={cn(
                  "relative flex h-10 items-center overflow-hidden rounded-md border text-sm",
                  mine ? "border-accent/60" : "border-line",
                  canListVoters && o.votes > 0 && "cursor-pointer hover:border-line-strong",
                )}
              >
                <div
                  aria-hidden
                  className={cn("absolute inset-y-0 left-0 transition-[width] duration-300 motion-reduce:transition-none", top ? "bg-accent-soft" : "bg-surface-2")}
                  style={{ width: `${pct}%` }}
                />
                <span className="relative flex min-w-0 flex-1 items-center gap-2 px-3">
                  {o.emoji && <span className="text-base leading-none">{o.emoji}</span>}
                  <span className={cn("truncate", top ? "font-semibold text-fg" : "text-fg-2")}>{o.text}</span>
                  {mine && <IconCheck className="icon size-4 shrink-0 text-accent" aria-label="Your vote" />}
                </span>
                <span className="relative shrink-0 px-3 text-xs font-medium text-fg-2 tabular">
                  <span className="sr-only">
                    {o.votes} {o.votes === 1 ? "vote" : "votes"},{" "}
                  </span>
                  {pct}%
                </span>
              </div>
            );
          const picked = picks.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              disabled={busy}
              role={poll.multiple ? "checkbox" : "radio"}
              aria-checked={picked}
              onClick={() => (poll.multiple ? setPicks((x) => (x.includes(o.id) ? x.filter((y) => y !== o.id) : [...x, o.id])) : void vote([o.id]))}
              className={cn(
                "flex h-10 w-full items-center gap-2.5 rounded-md border px-3 text-left text-sm transition-colors disabled:opacity-60",
                picked ? "border-accent bg-accent-soft text-fg" : "border-line text-fg hover:border-line-strong hover:bg-surface-2",
              )}
            >
              <span className={cn("flex size-4 shrink-0 items-center justify-center border", poll.multiple ? "rounded" : "rounded-full", picked ? "border-accent bg-accent text-accent-fg" : "border-line-strong")} aria-hidden>
                {picked && <IconCheck className="size-3" />}
              </span>
              {o.emoji && <span className="text-base leading-none">{o.emoji}</span>}
              <span className="truncate">{o.text}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-3">
        <span className="tabular">
          {poll.totalVoters} {poll.totalVoters === 1 ? "vote" : "votes"}
        </span>
        <span aria-hidden>·</span>
        <Tooltip label={fullDate(poll.endsAt)}>
          <span>{closed ? "Final results" : timeLeft(poll.endsAt)}</span>
        </Tooltip>
        <span className="ml-auto flex items-center gap-1">
          {canListVoters && (
            <Button size="sm" variant="ghost" icon={IconUsers} onClick={() => setVotesOf(poll.options.find((o) => o.votes > 0)?.id ?? poll.options[0].id)}>
              View votes
            </Button>
          )}
          {!closed && voted && canVote && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void vote([])}>
              Remove vote
            </Button>
          )}
          {!closed && canEnd && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(onEnd)}>
              End poll
            </Button>
          )}
          {!showResults && poll.multiple && (
            <Button size="sm" variant="primary" disabled={!picks.length} loading={busy} onClick={() => void vote(picks)}>
              Vote
            </Button>
          )}
        </span>
      </div>
      {canListVoters && <PollVotesDialog messageId={messageId!} poll={poll} optionId={votesOf} onOptionChange={setVotesOf} onClose={() => setVotesOf(null)} />}
    </div>
  );
}
